import { useEffect, useState } from 'react';

/**
 * The circle on the weddings grid: the couple's photograph, or their monogram.
 *
 * One component for both so the ring, the padding, and the aspect ratio are
 * defined once — a fallback that is a few pixels off the thing it replaces is
 * visible immediately in a four-across grid, where the eye has three correct
 * circles beside it for comparison.
 *
 * The `<img>` is owner-scoped: `/api/weddings/:id/photo` streams the bytes
 * against the planner's session, so it is a normal same-origin request that
 * carries the session cookie and resolves for nobody else.
 */
/**
 * Either a stored photo addressed by its wedding, or a local one the planner has
 * only just picked. A union rather than loose optional props, so a caller cannot
 * ask for a stored photo and forget to say which wedding's.
 */
type CouplePhotoProps = {
  monogram: string;
  alt: string;
  className?: string;
} & (
  | {
      weddingId: string;
      hasPhoto: boolean;
      /** Changed by the caller after an upload to defeat the browser cache — the
       *  URL is the wedding id, so a replacement photo reuses it. A counter where
       *  the caller did the upload itself, or the photo key where it only has the
       *  wedding: the key gets a fresh uuid per upload, so it changes exactly when
       *  the bytes do. */
      version?: number | string;
      src?: never;
    }
  | {
      /** An object URL for a file that is not on the server yet — the create
       *  dialog, where the wedding this photo belongs to does not exist until
       *  the planner saves the form. Null is the monogram, so a picker has one
       *  call site rather than a branch around this component. */
      src: string | null;
      weddingId?: never;
      hasPhoto?: never;
      version?: never;
    }
);

export default function CouplePhoto({
  weddingId,
  hasPhoto,
  monogram,
  alt,
  version = 0,
  src,
  className = '',
}: CouplePhotoProps) {
  // A key that exists but whose bytes will not load — a bucket rotated out from
  // under the row, a delete that half-landed — should show the monogram rather
  // than a broken-image glyph.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [weddingId, version, hasPhoto, src]);

  const url =
    src ??
    (hasPhoto && weddingId
      ? `/api/weddings/${weddingId}/photo${version ? `?v=${encodeURIComponent(version)}` : ''}`
      : null);
  const showPhoto = url !== null && !failed;

  return (
    <div
      className={`w-full rounded-full bg-[var(--color-surface-container-lowest)] p-1.5 shadow-ambient ring-1 ring-border ${className}`}
    >
      <div className="aspect-square overflow-hidden rounded-full">
        {showPhoto ? (
          <img
            src={url}
            alt={alt}
            loading="lazy"
            onError={() => setFailed(true)}
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-gradient-to-br from-[var(--color-tertiary-container)] via-[var(--color-surface-container)] to-[var(--color-secondary-container)]">
            <span className="display-tight text-2xl font-semibold text-[var(--color-primary-dim)] sm:text-3xl">
              {monogram}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
